import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, ArrowUpRight } from 'lucide-react';

const MODE_NAMES = { HP: 'Hardpoint', SND: 'Search & Destroy', CTL: 'Control' };
const percentage = (value) => value === null ? '-' : `${value.toFixed(1)}%`;
const metric = (value) => value === null ? '-' : value.toFixed(1);
const score = ({ wins, losses, total }) => total ? `${wins}-${losses}` : '-';
const trendText = (value, unit = 'pp') => value === null
  ? 'Not enough earlier maps'
  : `${value > 0 ? '↑' : value < 0 ? '↓' : '→'} ${value > 0 ? '+' : ''}${value.toFixed(1)} ${unit}`;
const trendDirection = (value) => value === null ? 'unavailable' : value > 0 ? 'up' : value < 0 ? 'down' : 'unchanged';
const dateLabel = (value) => {
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date);
};

const Section = ({ title, note, children, className = '' }) => (
  <section className={`v2-section ${className}`}>
    <header className="v2-section-head"><h2>{title}</h2>{note && <span>{note}</span>}</header>
    {children}
  </section>
);

const Trend = ({ value, unit, compact = false }) => (
  <span className={`v2-trend ${trendDirection(value)}${compact ? ' compact' : ''}`}
    aria-label={value === null ? 'Not enough earlier maps to calculate trend' :
      `Trend ${trendDirection(value)} ${Math.abs(value).toFixed(1)} ${unit || 'percentage points'} compared with earlier maps`}>
    {trendText(value, unit || 'pp')}
  </span>
);

const WinRateBar = ({ value }) => value === null ? null : (
  <div className="v2-rate-track" role="progressbar" aria-label="Win rate" aria-valuenow={value}
    aria-valuemin={0} aria-valuemax={100}>
    <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
  </div>
);

export const TeamFormCard = ({ data }) => <Section title="Team Form" note="Map results in current filter" className="v2-team-form">
  <div className="v2-team-metrics">
    <div className="v2-kpi primary"><span>Map win rate</span><strong>{percentage(data.record.rate)}</strong>
      <small>{data.record.total ? `${data.record.wins} wins` : 'No map results'}</small></div>
    <div className="v2-kpi"><span>Map record</span><strong>{score(data.record)}</strong>
      <small>{data.record.total ? `${data.record.wins - data.record.losses >= 0 ? '+' : ''}${data.record.wins - data.record.losses} differential` : 'No map results'}</small></div>
    <div className="v2-kpi"><span>Maps played</span><strong>{data.record.total}</strong><small>Current filter</small></div>
  </div>
  <div className="v2-form-line"><strong>Recent form <span>Last 10</span></strong>
    {data.recent.length ? <div className="v2-results-strip" aria-label="Recent map results, newest first">
      {data.recent.map((value, index) => <span className={value === 'W' ? 'win' : 'loss'} key={index}
        aria-label={`Map ${index + 1}: ${value === 'W' ? 'win' : 'loss'}`}>{value === 'W' ? 'WIN' : 'LOSS'}</span>)}</div> :
      <p>No dated match results in this filter.</p>}
  </div>
</Section>;

export const ModeHealth = ({ items }) => <Section title="Mode Health" note="Last 10 vs earlier maps">
  <div className="v2-mode-grid">{items.map((item) => <div className="v2-mode-item" key={item.mode}>
    <h3>{MODE_NAMES[item.mode]}</h3>
    {item.record.total ? <>
      <div className="v2-mode-main"><strong>{percentage(item.record.rate)}</strong><span>{score(item.record)} maps</span></div>
      <WinRateBar value={item.record.rate} />
      <div className="v2-mode-footer"><div><span>Last {item.recent.total}</span><strong>{percentage(item.recent.rate)}</strong>
        <small>{score(item.recent)}</small></div><Trend value={item.delta} /></div>
    </> : <p className="v2-empty-inline">No matches in this filter.</p>}
  </div>)}</div>
</Section>;

export const MapPoolHealth = ({ groups, onOpenMap }) => {
  const [selectedMode, setSelectedMode] = useState('HP');
  const modes = ['HP', 'SND', 'CTL'];
  const onTabKeyDown = (event, index) => {
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % modes.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + modes.length) % modes.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = modes.length - 1;
    else return;
    event.preventDefault();
    setSelectedMode(modes[next]);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next].focus();
  };
  return <Section title="Map Pool Health" note="Last 5 appearances per map" className="v2-map-section">
    <div className="v2-map-tabs" role="tablist" aria-label="Map pool mode">
      {modes.map((mode, index) => <button type="button" role="tab" key={mode} id={`map-tab-${mode}`}
        aria-controls={`map-panel-${mode}`} aria-selected={selectedMode === mode}
        tabIndex={selectedMode === mode ? 0 : -1} onClick={() => setSelectedMode(mode)}
        onKeyDown={(event) => onTabKeyDown(event, index)}>{MODE_NAMES[mode]}</button>)}
    </div>
    {groups.map(({ mode, maps }) => <div role="tabpanel" key={mode} id={`map-panel-${mode}`}
      aria-labelledby={`map-tab-${mode}`} hidden={mode !== selectedMode}>
      {maps.length ? <div className="v2-map-scroll"><table><thead><tr>
        <th scope="col">Map</th><th scope="col">Record</th><th scope="col">Win rate</th>
        <th scope="col">Last 5</th><th scope="col">Trend</th>
      </tr></thead><tbody>{maps.map((item) => <tr key={item.map}>
        <th scope="row"><button type="button" className="v2-map-open" onClick={(event) => onOpenMap?.(mode, item, event.currentTarget)}
          aria-label={`View ${MODE_NAMES[mode]} ${item.map} evidence`}>{item.map}<ArrowUpRight size={15} aria-hidden="true" /></button></th><td>{score(item.record)}</td>
        <td><div className="v2-map-rate"><WinRateBar value={item.record.rate} /><strong>{percentage(item.record.rate)}</strong></div></td>
        <td>{score(item.recent)} <small>({item.recent.total} maps)</small></td>
        <td><Trend value={item.delta} compact /></td>
      </tr>)}</tbody></table></div> : <p className="v2-empty-inline">No {mode} maps in this filter.</p>}
    </div>)}
  </Section>;
};

export const PlayerForm = ({ players, loading, onViewPlayer }) => <Section title="Player Form" note="Recent 5 vs earlier maps" className="v2-player-section">
  {loading ? <p className="v2-empty-inline">Loading player stats...</p> : players.length ?
    <div className="v2-player-list">{players.map((player) => <div className="v2-player-row" key={player.name}>
      <div className="v2-player-identity"><h3>{player.name}</h3><button type="button" onClick={() => onViewPlayer?.(player)}
        aria-label={`View ${player.name} in Player Dashboard`}>View Player <ArrowUpRight size={14} aria-hidden="true" /></button></div><div className="v2-player-modes">{player.modes.map((item) => <div key={item.mode}>
        <div className="v2-player-mode-label"><b>{item.mode}</b><span>{item.metric}</span></div>
        {item.count ? <><div className="v2-player-value"><strong>{metric(item.current)}</strong>
          <small>{item.count} maps</small></div>
          <div className="v2-player-recent"><span>Recent {metric(item.recent)} ({item.recentCount})</span>
            <Trend value={item.deltaPercent} unit="%" compact /></div></> : <span className="v2-no-data">No data</span>}
      </div>)}</div>
    </div>)}</div> : <div className="v2-empty-state"><strong>No player statistics match the current filters.</strong>
      <p>Try adjusting the opponent, competition, or date range.</p></div>}
</Section>;

const FlagIcon = ({ severity }) => severity === 'attention' ? <AlertTriangle aria-hidden="true" /> :
  severity === 'positive' ? <CheckCircle2 aria-hidden="true" /> : <Info aria-hidden="true" />;
const Flag = ({ flag, featured = false, onOpenEvidence }) => <li className={`v2-flag ${flag.severity}${featured ? ' featured' : ''}`}>
  <button type="button" className="v2-flag-open" onClick={(event) => onOpenEvidence?.(flag, event.currentTarget)}
    aria-label={`View evidence for ${flag.title}`}>
  <div className="v2-flag-top"><span className="v2-flag-kind"><FlagIcon severity={flag.severity} />
    {flag.severity === 'attention' ? 'Attention' : flag.severity === 'positive' ? 'Positive' : 'Info'}</span>
    <Trend value={flag.delta} unit={flag.type === 'playerTrend' ? '%' : 'pp'} compact /></div>
  <strong className="v2-flag-title">{flag.title}</strong><p>{flag.detail}</p>
  </button>
</li>;

export const CoachingFlags = ({ flags, onOpenEvidence }) => {
  const featuredIndex = flags.findIndex((flag) => flag.severity === 'attention');
  return <Section title="Coaching Flags" note="Changes with sufficient samples" className="v2-flags">
    {flags.length ? <ul>{featuredIndex >= 0 && <Flag key="featured" flag={flags[featuredIndex]} featured onOpenEvidence={onOpenEvidence} />}
      {flags.map((flag, index) => index === featuredIndex ? null :
        <Flag key={`${flag.type}-${flag.title}-${index}`} flag={flag} onOpenEvidence={onOpenEvidence} />)}</ul> :
      <p className="v2-empty-inline">No changes meet the current sample and change thresholds.</p>}
  </Section>;
};

export const RecentResults = ({ results }) => <Section title="Recent Results" note="Latest dated map results" className="v2-recent">
  {results.length ? <ol>{results.map((item, index) => <li key={`${item.id}-${index}`}>
    <time dateTime={item.date}>{dateLabel(item.date)}</time>
    <div className="v2-result-copy"><strong>{item.opponent ? `vs ${item.opponent}` : 'Opponent unavailable'}</strong>
      <span>{item.mode}{item.map ? ` · ${item.map}` : ''}</span>{item.matchType && <small>{item.matchType}</small>}</div>
    <div className="v2-result-outcome"><b className={item.result === 'W' ? 'win' : 'loss'}>
      {item.result === 'W' ? 'WIN' : 'LOSS'}</b>
      {item.pf !== null && item.pa !== null && <strong>{item.pf}-{item.pa}</strong>}</div>
  </li>)}</ol> : <p className="v2-empty-inline">No dated match results in this filter.</p>}
</Section>;
